import {
  AlertCircle,
  Dialog,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import React from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { useCreateEmailCycleStore } from "../../stores/Hooks";
import { Stack, Typography } from "@mui/material";
import { RecurringScheduleEmailForm } from "../forms/RecurringScheduleEmailForm";
import { SingleTimeScheduleEmailForm } from "../forms/SingleTimeScheduleEmailForm";
import { createSingleTimeScheduleModel } from "../../models/SingleTimeScheduleModel";

const SCHEDULE_EMAIL_DIALOG_WIDTH = "560px";

interface ScheduleEmailDialogProps {
  open: boolean;
  onSubmit: VoidFunction;
  onCancel: VoidFunction;
}

interface ScheduleEmailDialogConfig {
  label: string;
  form: React.ReactElement;
}

const ALERT_ICON_SIZE = "20px";

export const ScheduleEmailDialog: React.FC<ScheduleEmailDialogProps> = observer(
  ({ open, onSubmit, onCancel }) => {
    const { t } = useTranslation();
    const spacing = useSpacing();
    const typography = useTypography();
    const tokens = useFoundationColorTokens();
    const createEmailCycleStore = useCreateEmailCycleStore();
    const fallbackSingleTimeSchedule = React.useMemo(
      () => createSingleTimeScheduleModel(),
      [],
    );

    const getScheduleEmailDialogConfig = (): ScheduleEmailDialogConfig => {
      switch (createEmailCycleStore.scheduleType.type) {
        case "IMMEDIATE_SCHEDULE":
        case "SINGLE_TIME_SCHEDULE":
          return {
            label: t("emailCycles.scheduleEmail"),
            form: (
              <SingleTimeScheduleEmailForm
                date={
                  createEmailCycleStore.scheduleType.type ===
                  "SINGLE_TIME_SCHEDULE"
                    ? createEmailCycleStore.scheduleType.date
                    : fallbackSingleTimeSchedule.date
                }
                time={
                  createEmailCycleStore.scheduleType.type ===
                  "SINGLE_TIME_SCHEDULE"
                    ? createEmailCycleStore.scheduleType.time
                    : fallbackSingleTimeSchedule.time
                }
                scheduleMode={
                  createEmailCycleStore.scheduleType.type ===
                  "IMMEDIATE_SCHEDULE"
                    ? "IMMEDIATE"
                    : "LATER"
                }
                onScheduleModeChange={(scheduleMode) => {
                  if (scheduleMode === "IMMEDIATE") {
                    createEmailCycleStore.setImmediateScheduleType();
                    return;
                  }

                  createEmailCycleStore.setLaterScheduleType();
                }}
              />
            ),
          };
        case "RECURRING_SCHEDULE":
          return {
            label: t("emailCycles.recurringScheduleEmail"),
            form: (
              <RecurringScheduleEmailForm
                startDate={createEmailCycleStore.scheduleType.startDate}
                endDate={createEmailCycleStore.scheduleType.endDate}
                time={createEmailCycleStore.scheduleType.timeOfDay}
                daysOfWeek={createEmailCycleStore.scheduleType.daysOfWeek}
              />
            ),
          };
        default:
          return createEmailCycleStore.scheduleType satisfies never;
      }
    };
    const dialogProps = getScheduleEmailDialogConfig();

    return (
      <Dialog
        open={open}
        title={dialogProps.label}
        primaryButtonText={dialogProps.label}
        onPrimaryButtonClick={async () => {
          onSubmit();
        }}
        isPrimaryButtonDisabled={createEmailCycleStore.disableCreateEmail}
        secondaryButtonText={t("common.cancel")}
        onSecondaryButtonClick={onCancel}
        width={SCHEDULE_EMAIL_DIALOG_WIDTH}
        disableBackdropClick={true}
      >
        <Stack gap={spacing.spaceLG}>
          {/* Description */}
          <Stack direction="row" gap={spacing.spaceXS} color={tokens.label}>
            <AlertCircle
              height={ALERT_ICON_SIZE}
              width={ALERT_ICON_SIZE}
              style={{ flexShrink: 0 }}
            />
            <Typography {...typography.b2}>
              {t("emailCycles.scheduleEmailDialogDescription")}
            </Typography>
          </Stack>

          {/* Form */}
          {dialogProps.form}
        </Stack>
      </Dialog>
    );
  },
);
